/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/manager/EmployeePicker.tsx
 * PURPOSE: Ô chọn nhân viên (SC-009) — ô tìm NGAY ĐẦU → chips đã chọn →
 *          danh sách gom nhóm theo khu vực (+ "Chọn cả nhóm") → chân panel
 *          (đếm "N đã chọn"/"Tất cả nhân viên" + [Xoá lọc]). Nút "Tất cả
 *          (bỏ chọn)" KHÔNG thuộc component này — nó là `menu__item` RIÊNG,
 *          đứng NGOÀI/TRÊN, render bởi ManagerSchedulePage.tsx trong cùng
 *          `.menu.employeePickerMenu`.
 *          Nguồn thiết kế ĐÚNG: prototype sc009-build-prototype.html
 *          (`.emp-picker*`, `menuEmp()`) — component RIÊNG của SC-009, KHÔNG
 *          PHẢI mẫu SC-011 `ManagerAttendancePage.tsx` (260px, phẳng, không
 *          nhóm). Bản trước (2026-09-14) từng port nhầm theo
 *          WIREFRAME-V0-MASTER-REGISTRY.md §SC-009 ANNOTATION `[11c]` —
 *          annotation đó LỖI THỜI (chính prototype tự ghi chú lịch sử sai
 *          này ở khối comment CSS/JS "GỠ MẪU SC-011, DỰNG LẠI THEO COMPONENT
 *          RIÊNG"); coordinator xử lý đồng bộ lại wireframe registry riêng,
 *          không phải phạm vi file này. Giữ nhóm-theo-khu-vực + "Chọn cả
 *          nhóm" từ pattern màn "Giao việc" (GiaoViecModal, ManagerTasksPage.tsx,
 *          biên bản họp 25/08 mục 1 — A2), khớp đúng markup prototype. Quy mô
 *          ~100 NV/cơ sở — không thể chọn từng người một.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-14
 * COMPLIANCE: SDLC 6.5.0 + NQH Brand Design System + Zero Mock Tolerance
 * PILLAR: P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Manager — chọn nhiều nhân viên khi phân ca / lọc lưới phân ca
 * - Impact: Vận hành — chọn-nhanh hàng loạt thay vì từng người một
 * - Stakeholders: Branch Managers
 *
 * @technical-architecture
 * - Layer: Presentation Layer tái dùng (components/manager)
 * - Pattern: search input (CONTROLLED — caller giữ state để ghép vào nhãn nút
 *   + lọc lưới ngoài, xem ManagerSchedulePage `employeeSearch`) + danh sách
 *   checkbox nhóm theo khu vực + chips đã chọn + chân panel (đếm/Xoá lọc);
 *   lọc tìm KHÔNG DẤU (FIX-3); component thuần presentational — dữ liệu +
 *   handlers do caller truyền vào. KHÔNG chứa "Tất cả (bỏ chọn)" (xem PURPOSE).
 * - Dependencies: không (CSS module riêng, không import page nào)
 *
 * @cross-references
 * - Prototype (nguồn thiết kế đích): docs/02-design/04-ui-ux-design/design-imports/
 *   ui-ux-nqhbot/v0/schedule/manager/sc009-build-prototype.html
 *   (CSS `.emp-picker*` ~dòng 238-288; JS `menuEmp()` ~dòng 2787-2880)
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md
 *   §SC-009 ANNOTATION `[11c]` — LỖI THỜI trên kích thước panel/vị trí "Tất cả"/
 *   chân panel; KHÔNG dùng làm nguồn cho các điểm đó (coordinator đồng bộ lại sau)
 * - Pattern gốc nhóm: frontend/src/pages/manager/ManagerTasksPage.tsx (GiaoViecModal)
 * - Caller duy nhất: frontend/src/pages/manager/ManagerSchedulePage.tsx
 *   (bộ lọc "Nhân viên" — A2, thay dropdown ☑/☐ ký tự cũ)
 * ===============================================================================
 */

import React, { useMemo } from "react";
import styles from "./EmployeePicker.module.css";

export interface EmployeePickerItem {
  id: string;
  name: string;
  /** Mã NV — hiện dạng `<Mã> • <Tên>` (Luật 11, wireframe [11c]) */
  code?: string | null;
  /** Dòng meta nhỏ bên phải mỗi hàng (vd "Thu ngân · ca 07:00-17:00") */
  meta?: string;
  /** Tên khu vực/nhóm — picker gom hàng theo giá trị này */
  groupName: string;
  /** Khoá chọn — hiện mờ, "Chọn cả nhóm" cũng bỏ qua */
  disabled?: boolean;
}

interface EmployeePickerProps {
  items: EmployeePickerItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  /** "Chọn cả nhóm" — ids = các id chọn-được (không disabled) của nhóm */
  onSetGroup: (ids: string[], selected: boolean) => void;
  /** Ô tìm — CONTROLLED bởi caller (nhãn nút ngoài phải đọc được chuỗi tìm để
   * không mất dấu bộ lọc khi tấm đã đóng — state nội bộ cũ của component này
   * không "thoát ra" được; đúng behavior prototype `ST.filters.search`,
   * sc009-build-prototype.html dòng ~2743-2751). */
  search: string;
  onSearchChange: (value: string) => void;
  /** Chân panel "[Xoá lọc]" — xoá CẢ HAI vế (lựa chọn + chuỗi tìm). */
  onClearAll: () => void;
  loading?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
}

/** Bỏ dấu tiếng Việt để tìm không cần gõ dấu — cùng thuật toán FIX-3
 * (scheduleShared.noDiacritics); copy cục bộ để giữ component độc lập,
 * không import ngược lên pages/manager/schedule. */
const noDiacritics = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const EmployeePicker: React.FC<EmployeePickerProps> = ({
  items,
  selectedIds,
  onToggle,
  onSetGroup,
  search,
  onSearchChange,
  onClearAll,
  loading = false,
  searchPlaceholder = "Tìm theo tên…",
  // prototype menuEmp()/drawList(): chuỗi tìm không khớp ai → nguyên văn này
  // trong tấm (sc009-build-prototype.html dòng ~2837, `.emp-picker__state`).
  emptyText = "Không tìm thấy nhân viên phù hợp.",
}) => {
  const filteredItems = useMemo(() => {
    const q = noDiacritics(search.trim());
    if (!q) return items;
    return items.filter(
      (item) =>
        noDiacritics(item.name).includes(q) ||
        (item.code != null && noDiacritics(item.code).includes(q)),
    );
  }, [items, search]);

  const groups = useMemo(() => {
    const map = new Map<string, EmployeePickerItem[]>();
    for (const item of filteredItems) {
      const key = item.groupName;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries());
  }, [filteredItems]);

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.id)),
    [items, selectedIds],
  );

  // prototype empFilterOn() — dùng để disable nút "Xoá lọc" khi không có gì
  // để xoá (sc009-build-prototype.html dòng ~2756).
  const hasActiveFilter = selectedIds.size > 0 || search.trim().length > 0;
  // prototype fEmpCount (dòng ~2815): đếm SỐ ĐÃ CHỌN — "Tất cả nhân viên" khi
  // chưa chọn ai, KHÔNG PHẢI số mục đang hiện sau lọc tìm (`filteredItems.length`
  // là bug lượt trước — 2 khái niệm khác nhau: "đang hiện trong tấm" vs "đã chọn").
  const footCount =
    selectedIds.size === 0 ? "Tất cả nhân viên" : `${selectedIds.size} đã chọn`;

  return (
    <div className={styles.root}>
      {/* prototype: ô tìm là con ĐẦU TIÊN của `.emp-picker` — "Tất cả (bỏ
          chọn)" đứng NGOÀI, do caller render (xem ManagerSchedulePage.tsx). */}
      <input
        type="text"
        className={styles.searchInput}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        aria-label={searchPlaceholder}
      />

      {selectedItems.length > 0 && (
        <div className={styles.selectedChips}>
          {selectedItems.map((item) => (
            <span key={item.id} className={styles.chip}>
              {item.name}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => onToggle(item.id)}
                aria-label={`Bỏ chọn ${item.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.rosterList}>
        {loading ? (
          <div className={styles.stateRow}>Đang tải danh sách...</div>
        ) : groups.length === 0 ? (
          <div className={styles.stateRow}>{emptyText}</div>
        ) : (
          groups.map(([groupName, groupItems]) => {
            const selectableIds = groupItems.filter((i) => !i.disabled).map((i) => i.id);
            const allSelected =
              selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
            return (
              <div key={groupName}>
                <div className={styles.rosterGroupHead}>
                  <span>
                    {groupName} ({groupItems.length})
                  </span>
                  <label className={styles.rosterToggle} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      onChange={() => onSetGroup(selectableIds, !allSelected)}
                    />
                    Chọn cả nhóm
                  </label>
                </div>
                {groupItems.map((item) => (
                  <div
                    key={item.id}
                    className={`${styles.rosterItem} ${item.disabled ? styles.rosterItemDisabled : ""}`}
                    onClick={() => !item.disabled && onToggle(item.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      disabled={item.disabled}
                      onChange={() => onToggle(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Chọn ${item.name}`}
                    />
                    <span className={styles.rosterItemName}>
                      {/* Luật 11 (`<Mã> • <Tên>`) — cùng khuôn cột "Nhân viên"
                          của lưới (formatEmployeeLabel trong ManagerSchedulePage.tsx). */}
                      {item.code ? `${item.code} • ${item.name}` : item.name}
                    </span>
                    {item.meta && <span className={styles.rosterItemMeta}>{item.meta}</span>}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {/* prototype `.emp-picker__foot` (`#fEmpCount` + `#fEmpClear`): đếm số
          ĐÃ CHỌN ("Tất cả nhân viên" khi rỗng) + [Xoá lọc] (xoá CẢ HAI vế). */}
      <div className={styles.pickFoot}>
        <span className={styles.pickFootCount}>{footCount}</span>
        <button
          type="button"
          className={styles.clearBtn}
          disabled={!hasActiveFilter}
          onClick={onClearAll}
        >
          Xoá lọc
        </button>
      </div>
    </div>
  );
};

export default EmployeePicker;
